import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTY_PACKAGES = [
  {
    name: 'Mini Splash',
    price: 120,
    color: 'from-pink-500 to-rose-500',
    border: 'border-pink-400',
    inclusions: ['Up to 5 painters', '30-min session', 'All materials & protective gear', '1 canvas per child']
  },
  {
    name: 'Party Pack',
    price: 180,
    color: 'from-purple-500 to-violet-500',
    border: 'border-purple-400',
    popular: true,
    inclusions: ['Up to 8 painters', '1-hour session', 'All materials & gear', '1 canvas each', 'Dedicated party host']
  },
  {
    name: 'Ultimate Rage Bash',
    price: 350,
    color: 'from-amber-500 to-yellow-500',
    border: 'border-amber-400',
    inclusions: ['Up to 10 painters', '1-hour session', 'All materials & gear', '1 large canvas each + group canvas', 'Party host', 'Extra 30 min in party room']
  }
];

const initialForm = {
  parent_name: '',
  email: '',
  phone: '',
  party_date: '',
  child_age: '',
  group_size: '',
  message: ''
};

export const PartyInquiryDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!selectedPackage) {
      toast.error('Please pick a party package (or "Not sure yet")');
      return;
    }
    if (!form.parent_name || !form.email || !form.phone || !form.party_date || !form.child_age || !form.group_size) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/party-inquiries`, {
        ...form,
        package: selectedPackage,
        group_size: parseInt(form.group_size) || 1
      });
      if (response.data.success) {
        toast.success('🎂 Party request sent!', {
          description: "We'll reach out shortly to plan the perfect celebration."
        });
        setForm(initialForm);
        setSelectedPackage('');
        onOpenChange(false);
      } else {
        toast.error(response.data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send request', {
        description: 'Please try again later or contact us directly.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="party-inquiry-dialog">
        <DialogHeader>
          <DialogTitle>🎂 Birthday Party Request</DialogTitle>
          <DialogDescription>
            Step 1: Pick a package. Step 2: Tell us about the celebration - we'll confirm everything with you!
          </DialogDescription>
        </DialogHeader>
        
        {/* Package Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PARTY_PACKAGES.map((pkg) => (
            <button
              key={pkg.name}
              type="button"
              onClick={() => setSelectedPackage(`${pkg.name} ($${pkg.price})`)}
              data-testid={`party-package-${pkg.name.toLowerCase().replace(/ /g, '-')}`}
              className={`relative text-left p-4 rounded-xl border-3 transition-all duration-200 ${
                selectedPackage === `${pkg.name} ($${pkg.price})`
                  ? `bg-gradient-to-br ${pkg.color} text-white ${pkg.border} scale-[1.02] shadow-lg`
                  : 'bg-white border-gray-200 hover:border-pink-300 hover:shadow-md'
              }`}
            >
              {pkg.popular && (
                <span className="absolute -top-2 right-2 bg-yellow-400 text-gray-900 text-[10px] font-black px-2 py-0.5 rounded-full">MOST POPULAR</span>
              )}
              <p className={`font-black text-base ${selectedPackage === `${pkg.name} ($${pkg.price})` ? 'text-white' : 'text-gray-900'}`}>{pkg.name}</p>
              <p className={`font-black text-2xl mb-2 ${selectedPackage === `${pkg.name} ($${pkg.price})` ? 'text-white' : 'text-pink-600'}`}>${pkg.price}</p>
              <ul className={`text-xs space-y-1 ${selectedPackage === `${pkg.name} ($${pkg.price})` ? 'text-white/90' : 'text-gray-600'}`}>
                {pkg.inclusions.map((inc) => (
                  <li key={inc}>✓ {inc}</li>
                ))}
              </ul>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSelectedPackage('Not sure yet')}
          data-testid="party-package-not-sure"
          className={`w-full p-2.5 rounded-lg border-2 text-sm font-semibold transition-all ${
            selectedPackage === 'Not sure yet'
              ? 'bg-gray-800 text-white border-gray-700'
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
          }`}
        >
          Not sure yet - help me choose
        </button>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-sm font-medium text-gray-700">Parent Name *</label>
            <Input placeholder="Your name" value={form.parent_name} onChange={update('parent_name')} data-testid="party-parent-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <Input type="email" placeholder="you@email.com" value={form.email} onChange={update('email')} data-testid="party-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone *</label>
              <Input type="tel" placeholder="(701) 123-4567" value={form.phone} onChange={update('phone')} data-testid="party-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Preferred Party Date *</label>
            <Input type="date" value={form.party_date} onChange={update('party_date')} data-testid="party-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700">Birthday Child's Age *</label>
              <Input placeholder="e.g., 10" value={form.child_age} onChange={update('child_age')} data-testid="party-child-age-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Group Size *</label>
              <Input type="number" min="1" placeholder="e.g., 8" value={form.group_size} onChange={update('group_size')} data-testid="party-group-size-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Anything else we should know?</label>
            <Textarea placeholder="Theme ideas, allergies, special requests..." rows={3} value={form.message} onChange={update('message')} data-testid="party-message-input" />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold"
            data-testid="party-submit-button"
          >
            {submitting ? 'Sending...' : 'Send Party Request 🎉'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
